import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ProgramPriorityEnum } from '@/features/programs/types';
import { mediaManagerClient } from '@/lib/axios';
import { MutationConfig, queryClient } from '@/lib/react-query';
import { useNotificationStore } from '@/stores/notifications';
import { emptyStringValues } from '@/utils/objects';

import { TProgramBroadcast } from '../types';

import { broadcastScheduleQueryKeys } from './query-keys';

interface EnrichmentParameters {
  stt?: boolean | null;
  logos?: boolean | null;
  peoples?: boolean | null;
  ocr?: boolean | null;
  advertising?: boolean | null;
}

interface ProgramParameters {
  logo?: string | null;
  logo_presigned_url?: string | null;
  enrichments?: EnrichmentParameters | null;
}

export type TCreateProgramBroadcast = {
  name: string;
  start_datetime: string;
  end_datetime: string;
  delivery_datetime?: string | null;
  channel_id: string;
  language_id: string;
  program_id?: string | null;
  audience_program_source_id?: string | null;
  description?: string | null;
  priority?: ProgramPriorityEnum | null;
  parameters?: ProgramParameters | null;
  current_user_id?: string | null;
  program_broadcast_id?: string | null;
};

export async function createProgramBroadcast(
  body: TCreateProgramBroadcast,
  // Lets a caller creating many emissions in a row abort the one in flight;
  // `useCreateProgramBroadcast` never passes it, so a single creation is
  // unaffected.
  options?: { signal?: AbortSignal }
): Promise<TProgramBroadcast> {
  return mediaManagerClient.post('/api/v1/program_broadcasts', emptyStringValues(body), {
    signal: options?.signal,
  });
}

type MutationFn = typeof createProgramBroadcast;

type TUsePostBroadcastersParams = {
  config?: MutationConfig<MutationFn>;
};

export function useCreateProgramBroadcast({ config = {} }: TUsePostBroadcastersParams = {}) {
  const { t } = useTranslation();
  const { addErrorNotification, addSuccessNotification } = useNotificationStore();

  return useMutation({
    ...config,
    mutationFn: createProgramBroadcast,
    onError(error) {
      addErrorNotification(t('El programa no se ha creado'), t('Ha habido un fallo en el proceso'));
      console.error(error);
    },
    onSuccess(data, variables, context) {
      config.onSuccess?.(data, variables, context);
      addSuccessNotification(t('Se ha creado el programa'), t('El proceso ha sido un éxito'));
      queryClient.invalidateQueries({ queryKey: broadcastScheduleQueryKeys.all });
    },
  });
}
